<template>
  <div class="notification" :class="[variant]">
    <div class="icon">
      <success-icon v-if="variant === 'success'" />
      <error-icon v-else-if="variant === 'error'" />
      <download-icon v-else-if="variant === 'download'" />
      <info-icon v-else />
    </div>
    <p class="flex flex-col">
      <span class="font-bold !text-neutral whitespace-pre-line">
        <component :is="title" v-if="typeof title !== 'string'" />
        <template v-else>{{ title }}</template>
      </span>
    </p>
    <div class="close-icon" @click="emit('close')">
      <cross-icon />
    </div>
  </div>
</template>

<script lang="tsx" setup>
import InfoIcon from '@images/exclamation.svg?component';
import ErrorIcon from '@images/error.svg?component';
import SuccessIcon from '@images/success.svg?component';
import CrossIcon from '@images/close.svg?component';
import DownloadIcon from '@images/download.svg?component';
import type { Component } from 'vue';

export type NotificationVariant = 'error' | 'info' | 'success' | 'download';

interface Props {
  variant?: NotificationVariant;
  title: string | JSX.Element | Component;
}

const emit = defineEmits(['close']);

withDefaults(defineProps<Props>(), {
  variant: 'info',
});
</script>

<style lang="scss" scoped>
.notification {
  @apply flex items-center gap-x-7.5 pl-2.5 pr-5 py-2.5 ml-auto;
  @apply border border-info-blue;
  @apply rounded-4;
  @apply bg-white;
  @apply text-neutral;
  @apply w-fit;
  width: max-content;
  min-width: px(400);
  
  :deep(a) {
    @apply text-info-blue underline;

    &:hover {
      @apply text-primary;
    }
  }

  .icon {
    @apply w-13 h-13 p-2;
    @apply rounded-full ring-8 ring-offset-1 ring-info-blue-10 ring-inset;
    flex: 0 0 auto;

    & > * {
      @apply w-full h-full;
    }

      :deep(path) {
        @apply fill-info-blue;
      }
  }

  &.error {
    @apply text-red-130;
    @apply border-primary;

    .icon {
      @apply ring-red-10;
      @apply bg-white;

      :deep(path) {
        fill: theme("colors.red.120");
      }
    }
  }

  &.success {
    @apply text-success-green;
    @apply border-success-green;

    .icon {
      @apply ring-success-green ring-success-green-20;
      @apply bg-white;

      :deep(path) {
        fill: theme("colors.success-green.100");
      }
    }
  }

  .close-icon {
    @apply w-5 h-5 text-neutral stroke-2 ml-auto cursor-pointer;
  }
}
</style>
